import React, { useState, useEffect, useRef, useCallback } from 'react';
import { navigate } from 'gatsby';

import { COOKIE_EXACT_TIME, getCookie } from 'helpers';

import {
  validateName,
  validateEmail,
  validatePhone,
  validateCompany,
  validateMessage,
  Validation,
} from './helpers/validation-helper';
import { IFileItem } from './components/FileInput';
import { Attachment, InputField } from './components';
import withToastProvider from './toast/withToastProvider';
import useToast from './toast/useToast';
import { URLEnums } from '../../enum/URLEnum';
import { FormProps } from '../../interfaces';
import CompanyContactsDetailed from './components/Offices/CompanyContactsDetailed';

const getSize = (files: File[]) => {
  return files.reduce((prev, cur) => {
    return prev + cur.size;
  }, 0);
};

const maxTotalSizeBytes = 11000000;
const maxFilesCount = 20;

const ContactUs = ({ TextareaDefaultValue, isForm = false }: FormProps) => {
  const mcode = '8daa8b05c10f4703b6da51e67ce63ef0';

  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<IFileItem[]>([]);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [project, setProject] = useState<string>(TextareaDefaultValue || '');
  const [company, setCompany] = useState<string>('');
  const [isNameValid, setIsNameValid] = useState<Validation>({ isValid: true, validationMessage: '' });
  const [isEmailValid, setIsEmailValid] = useState<Validation>({ isValid: true, validationMessage: '' });
  const [isPhoneValid, setIsPhoneValid] = useState<Validation>({ isValid: true, validationMessage: '' });
  const [isProjectValid, setIsProjectValid] = useState<Validation>({ isValid: true, validationMessage: '' });
  const [isCompanyValid, setIsCompanyValid] = useState<Validation>({ isValid: true, validationMessage: '' });
  const [isFileSizeValid, setIsFileSizeValid] = useState<Validation>({ isValid: true, validationMessage: '' });
  const [isFilesMaxCountValid, setIsFilesMaxCountValid] = useState<boolean>(true);
  const [isFormSending, setIsFormSending] = useState<boolean>(false);
  const toast = useToast();
  const validateForm = useCallback(() => {
    const isNameInputValid = validateName(name);
    const isEmailInputValid = validateEmail(email);
    const isPhoneInputValid = validatePhone(phone);
    const isProjectInputValid = validateMessage(project);
    const isCompanyInputValid = validateCompany(company);

    setIsNameValid(isNameInputValid);
    setIsEmailValid(isEmailInputValid);
    setIsPhoneValid(isPhoneInputValid);
    setIsProjectValid(isProjectInputValid);
    setIsCompanyValid(isCompanyInputValid);

    return (
      isNameInputValid.isValid &&
      isEmailInputValid.isValid &&
      isPhoneInputValid.isValid &&
      isProjectInputValid.isValid &&
      isCompanyInputValid.isValid &&
      isFileSizeValid &&
      isFilesMaxCountValid
    );
  }, [company, email, isFileSizeValid, isFilesMaxCountValid, name, phone, project]);

  const handleOnSuccessSendEmail = () => {
    (window as any).dataLayer?.push({
      event: 'ga_event',
      eventCategory: 'Form',
      eventAction: 'Request Sent',
      exactTime: getCookie(COOKIE_EXACT_TIME),
    });

    return navigate(URLEnums.Thanks);
  };

  useEffect(() => {
    window.contactFormConfig = {
      recaptchaKey: process.env.GOOGLE_CAPTCHA_API_KEY,
      apiKey: process.env.CONTACT_FORM_API_KEY,
      apiUri: process.env.CONTACT_FORM_API_URL,
      getCustomData: () => {
        return {
          exactTime: getCookie(COOKIE_EXACT_TIME),
          mcode,
        };
      },
      onSuccess: () => {
        handleOnSuccessSendEmail();
      },
      onSubmit: () => {
        setIsFormSending(true);
      },
      onError: (serverError: any) => {
        validateForm();

        toast.add({
          title: 'Server error',
          description: serverError.toString() || 'Delivery failed',
        });
        (window as any).dataLayer?.push({
          event: 'ga_event',
          eventCategory: 'Form',
          eventAction: 'Request Error',
          exactTime: getCookie(COOKIE_EXACT_TIME),
        });
        setIsFormSending(false);
      },
    };
  }, [files, toast, validateForm]);

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    const isFormValid = validateForm();

    if (isFormValid) {
      setIsFormSending(true);

      window.contactForm.submit();
    }

    return true;
  };

  const handleOnNameChange = (value: string) => {
    setName(value);
  };

  const handleOnEmailChange = (value: string) => {
    setEmail(value);
  };

  const handleOnPhoneChange = (value: string) => {
    setPhone(value.trim());
  };

  const handleOnProjectChange = (value: string) => {
    setProject(value);
  };

  const handleOnCompanyChange = (value: string) => {
    setCompany(value);
  };

  interface Props {
    file?: {
      lastModified?: number;
      lastModifiedDate?: string;
      name?: string;
      size?: number;
      type?: string;
      webkitRelativePath?: string;
    };
    isChecked?: boolean;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateInputFileList = (filesArray: Props[]) => {
    const list = new DataTransfer();

    filesArray.forEach((item) => {
      // eslint-disable-next-line no-unused-expressions
      item instanceof Blob;
      // eslint-disable-next-line no-unused-expressions
      item.file &&
        list.items.add(new File([item.file as BlobPart], item.file.name as string, { type: item.file.type }));
    });

    if (fileInput && fileInput.current && fileInput.current.files) {
      fileInput.current.files = list.files;
    }
  };

  useEffect(() => {
    updateInputFileList(files);
    setIsFilesMaxCountValid(files.length <= maxFilesCount);
  }, [files, updateInputFileList]);

  const handleChange = () => {
    if (fileInput && fileInput.current && fileInput.current.files) {
      const selectedFiles = Object.values(fileInput.current.files);
      const addedFilesSize = getSize(files.map((item) => item.file));
      const noZeroSizeFiles = selectedFiles.filter((file) => file.size > 0);
      const uniqNameFiles = noZeroSizeFiles.filter((file) => !files.find((item) => item.file.name === file.name));
      const newFilesSize = getSize(uniqNameFiles);

      if (selectedFiles.length !== noZeroSizeFiles.length) {
        toast.add({
          title: 'The file size is 0 MB.',
          description: 'Your file has not been added. Please choose a file with valid size.',
        });
      }

      if (selectedFiles.length > maxFilesCount) {
        toast.add({
          title: 'File limit exceeded.',
          description: 'Please select up to 20 files.',
        });
      }

      if (noZeroSizeFiles.length !== uniqNameFiles.length) {
        toast.add({
          title: 'Same file has been uploaded.',
          description: 'File already uploaded. Please choose a new one.',
        });
      }

      setIsFileSizeValid(addedFilesSize + newFilesSize < maxTotalSizeBytes);
      setIsFilesMaxCountValid(selectedFiles.length <= maxFilesCount);

      setFiles(files.concat(uniqNameFiles.map((file) => ({ isChecked: false, file }))));
    }
  };

  const handleRemoveFiles = () => {
    const filteredFiles = files.filter((file) => !file.isChecked);

    setIsFileSizeValid(getSize(filteredFiles.map((item) => item.file)) < maxTotalSizeBytes);
    setIsFilesMaxCountValid(files.length <= maxFilesCount);

    setFiles(filteredFiles);
  };

  const handleFileItemChange = (fileName: string) => {
    const selectedFile = files.find((fileItem) => fileItem.file.name === fileName);

    if (selectedFile) {
      selectedFile.isChecked = !selectedFile.isChecked;
    }

    setFiles(files);
  };

  const loadWidget = () => {
    const script = document.createElement('script');

    script.src = `${process.env.CONTACT_FORM_WIDGET_URL}`;
    script.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(script);
  };

  const handleFormFocus = () => {
    // eslint-disable-next-line no-unused-expressions
    window.contactForm || loadWidget();
  };

  const CONTACT_US = 'Contact us';

  return (
    <div className="section-background contact-form-container has-no-margin-bottom">
      <div id="contact-form-container" className="container main-grid">
        <h2 className="h1-title">{CONTACT_US}</h2>
        <form id="contact-form" action="" method="" onFocus={handleFormFocus} className="contact-form">
          <div className="main-grid-halved">
            <div className={!isNameValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
              <InputField
                id="name"
                type="text"
                placeHolder="Full name"
                validation={{
                  isValid: isNameValid.isValid,
                  validationMessage: isNameValid.validationMessage,
                }}
                onChange={handleOnNameChange}
                disabled={isFormSending}
                contactFormRole="input"
              />
            </div>
            <div className={!isEmailValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
              <InputField
                id="email"
                type="email"
                placeHolder="E-mail"
                contact-form-role="input"
                validation={{
                  isValid: isEmailValid.isValid,
                  validationMessage: isEmailValid.validationMessage,
                }}
                onChange={handleOnEmailChange}
                disabled={isFormSending}
                contactFormRole="input"
              />
            </div>
            <div className={!isPhoneValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
              <InputField
                id="phone"
                type="tel"
                placeHolder="Phone number"
                validation={{
                  isValid: isPhoneValid.isValid,
                  validationMessage: isPhoneValid.validationMessage,
                }}
                onChange={handleOnPhoneChange}
                disabled={isFormSending}
                contactFormRole="input"
              />
            </div>
            <div className="main-grid-halved-item">
              <InputField
                id="company"
                type="text"
                placeHolder="Company"
                validation={{
                  isValid: isCompanyValid.isValid,
                  validationMessage: isCompanyValid.validationMessage,
                }}
                onChange={handleOnCompanyChange}
                disabled={isFormSending}
                contactFormRole="input"
              />
            </div>
          </div>
          <InputField
            id="message"
            type="textarea"
            placeHolder="How can we help you?"
            contactFormRole="input"
            defaultValue={TextareaDefaultValue || ''}
            validation={{
              isValid: isProjectValid.isValid,
              validationMessage: isProjectValid.validationMessage,
            }}
            onChange={handleOnProjectChange}
            disabled={isFormSending}
          />
          <Attachment
            inputRef={fileInput}
            files={files}
            onChange={handleChange}
            onItemChange={handleFileItemChange}
            onRemove={handleRemoveFiles}
            onClick={handleSubmit}
            isFileSizeValid={isFileSizeValid}
            isFilesMaxCountValid={isFilesMaxCountValid}
            disabled={isFormSending}
            sending={isFormSending}
            contactFormRole="input"
          />
        </form>
      </div>
      {isForm && <CompanyContactsDetailed />}
    </div>
  );
};

export default withToastProvider(ContactUs);
